import type { LaunchLocale } from "../../locales/src/locales";
/** Screen-specific guidance from the original failure matrix. These are instructions,
 * never a claim that eligibility, a refund, a scan or a recovery has succeeded. */
const messages = {
  B01: [
    "Your search and filters are retained. Retry reading results.",
    "Se conservan la búsqueda y los filtros. Vuelve a consultar.",
    "La recherche et les filtres sont conservés. Relancez la lecture.",
    "Suche und Filter bleiben erhalten. Ergebnisse erneut laden.",
    "検索条件と絞り込みは保持されます。結果を再読み込みしてください。",
  ],
  B02: [
    "Remove one filter below or change your search. Your destination stays unchanged.",
    "Quita un filtro o cambia la búsqueda. El destino no cambia.",
    "Retirez un filtre ou modifiez la recherche. La destination reste inchangée.",
    "Einen Filter entfernen oder die Suche ändern. Das Ziel bleibt unverändert.",
    "絞り込みを一つ解除するか検索を変更してください。配送先は変わりません。",
  ],
  B03: [
    "Check availability and the item details before you buy.",
    "Comprueba la disponibilidad y los detalles del artículo antes de comprar.",
    "Vérifiez la disponibilité et les détails de l’article avant d’acheter.",
    "Prüfen Sie Verfügbarkeit und Artikeldetails, bevor Sie kaufen.",
    "購入する前に、在庫状況と商品の詳細をご確認ください。",
  ],
  B04: [
    "Existing purchases remain reachable from your order history during a sales pause.",
    "Puedes acceder a compras anteriores desde tus pedidos durante una pausa de ventas.",
    "Les achats existants restent accessibles depuis vos commandes pendant une pause des ventes.",
    "Bestehende Käufe bleiben während einer Verkaufspause über die Bestellungen erreichbar.",
    "販売休止中も注文履歴から購入済みの商品にアクセスできます。",
  ],
  B05: [
    "Sign in to the account that owns this collection to see it.",
    "Inicia sesión con la cuenta propietaria de esta colección para verla.",
    "Connectez-vous au compte propriétaire de cette collection pour la voir.",
    "Melden Sie sich mit dem Konto an, dem diese Sammlung gehört, um sie zu sehen.",
    "このコレクションを見るには、所有しているアカウントでログインしてください。",
  ],
  B06: [
    "Refresh claim availability. Recipient addresses remain private.",
    "Actualiza la disponibilidad. La dirección del destinatario sigue siendo privada.",
    "Actualisez la disponibilité. L’adresse du destinataire reste privée.",
    "Verfügbarkeit aktualisieren. Die Empfängeradresse bleibt privat.",
    "受取状況を更新してください。受取人の住所は非公開のままです。",
  ],
  B07: [
    "Check each changed item, currency and delivery total before you continue.",
    "Revisa cada artículo modificado, la moneda y el total de envío antes de continuar.",
    "Vérifiez chaque article modifié, la devise et le total de livraison avant de continuer.",
    "Prüfen Sie jeden geänderten Artikel, die Währung und die Versandsumme, bevor Sie fortfahren.",
    "続行する前に、変更された商品、通貨、送料の合計をご確認ください。",
  ],
  B08: [
    "Wait for any unfinished payment to settle first. If the total changed, review it again before paying.",
    "Espera primero a que termine cualquier pago pendiente. Si el total cambió, revísalo de nuevo antes de pagar.",
    "Attendez d’abord la fin de tout paiement en cours. Si le total a changé, vérifiez-le à nouveau avant de payer.",
    "Warten Sie zuerst, bis offene Zahlungen abgeschlossen sind. Hat sich die Summe geändert, prüfen Sie sie vor dem Bezahlen erneut.",
    "まず処理中の支払いが完了するまでお待ちください。合計が変わった場合は、支払う前にもう一度ご確認ください。",
  ],
  B09: [
    "Check your order reference. Don’t pay again while the result is still unknown.",
    "Consulta la referencia de tu pedido. No vuelvas a pagar mientras el resultado siga sin conocerse.",
    "Vérifiez la référence de votre commande. Ne payez pas à nouveau tant que le résultat est inconnu.",
    "Prüfen Sie Ihre Bestellnummer. Zahlen Sie nicht erneut, solange das Ergebnis noch offen ist.",
    "注文番号をご確認ください。結果がわかるまで、もう一度支払わないでください。",
  ],
  B10: [
    "To see receipts for a guest order, first confirm it’s yours.",
    "Para ver los recibos de un pedido como invitado, confirma primero que es tuyo.",
    "Pour voir les reçus d’une commande invité, confirmez d’abord qu’elle vous appartient.",
    "Um Belege einer Gastbestellung zu sehen, bestätigen Sie zuerst, dass sie Ihnen gehört.",
    "ゲスト注文の領収書を見るには、まずご自身の注文であることを確認してください。",
  ],
  B11: [
    "Compare the updated scope, proof, price and expiry. Accepting an earlier version doesn’t approve these changes.",
    "Compara el alcance, la prueba, el precio y la caducidad actualizados. Aceptar una versión anterior no aprueba estos cambios.",
    "Comparez le contenu, la maquette, le prix et l’échéance mis à jour. Accepter une version précédente n’approuve pas ces changements.",
    "Vergleichen Sie Umfang, Entwurf, Preis und Ablauf der neuen Version. Die Zustimmung zu einer früheren Version gilt nicht für diese Änderungen.",
    "更新された内容・校正・価格・期限を比べてください。以前の版を承認していても、この変更は承認されません。",
  ],
  B12: [
    "Your draft is kept. Check whether your last message was sent, and wait for attachments to finish their safety scan.",
    "Tu borrador se conserva. Comprueba si se envió tu último mensaje y espera a que los adjuntos terminen su análisis de seguridad.",
    "Votre brouillon est conservé. Vérifiez si votre dernier message est parti et attendez la fin de l’analyse de sécurité des pièces jointes.",
    "Ihr Entwurf bleibt erhalten. Prüfen Sie, ob Ihre letzte Nachricht gesendet wurde, und warten Sie die Sicherheitsprüfung der Anhänge ab.",
    "下書きは保持されています。前回のメッセージが送信されたか確認し、添付ファイルの安全チェックが終わるまでお待ちください。",
  ],
  B13: [
    "Review the remaining refundable components for each purchased item.",
    "Revisa los componentes reembolsables restantes de cada artículo comprado.",
    "Vérifiez les montants encore remboursables pour chaque article acheté.",
    "Verbleibende erstattbare Bestandteile je gekauftem Artikel prüfen.",
    "購入明細ごとの返金可能な残額を確認してください。",
  ],
  B14: [
    "Check the latest decision, the deadline and how you can appeal before continuing.",
    "Revisa la última decisión, el plazo y cómo puedes recurrir antes de continuar.",
    "Consultez la dernière décision, le délai et les possibilités de recours avant de continuer.",
    "Prüfen Sie die letzte Entscheidung, die Frist und Ihre Einspruchsmöglichkeiten, bevor Sie fortfahren.",
    "続行する前に、最新の判断、期限、異議申し立ての方法をご確認ください。",
  ],
  B15: [
    "Only the buyer of this item can review it. Your draft stays private.",
    "Solo quien compró este artículo puede reseñarlo. Tu borrador sigue siendo privado.",
    "Seul l’acheteur de cet article peut laisser un avis. Votre brouillon reste privé.",
    "Nur wer diesen Artikel gekauft hat, kann ihn bewerten. Ihr Entwurf bleibt privat.",
    "この商品のレビューを書けるのは購入者だけです。下書きは非公開のままです。",
  ],
  B16: [
    "Gift credit isn’t available yet, so no money can be added here.",
    "El crédito de regalo aún no está disponible, así que aquí no se puede añadir dinero.",
    "Le crédit cadeau n’est pas encore disponible, aucun montant ne peut donc être ajouté ici.",
    "Geschenkguthaben ist noch nicht verfügbar, daher kann hier kein Geld eingezahlt werden.",
    "ギフトクレジットはまだ利用できないため、ここで入金することはできません。",
  ],
  A01: [
    "This verification link has expired. Start again to get a new one. For privacy, we never say whether an account exists.",
    "Este enlace de verificación ha caducado. Empieza de nuevo para recibir otro. Por privacidad, nunca indicamos si una cuenta existe.",
    "Ce lien de vérification a expiré. Recommencez pour en recevoir un nouveau. Par confidentialité, nous n’indiquons jamais si un compte existe.",
    "Dieser Bestätigungslink ist abgelaufen. Beginnen Sie neu, um einen neuen zu erhalten. Aus Datenschutzgründen sagen wir nie, ob ein Konto existiert.",
    "この確認リンクは期限切れです。最初からやり直して新しいリンクを受け取ってください。プライバシー保護のため、アカウントの有無はお知らせしません。",
  ],
  A02: [
    "Security changes need you to confirm it’s you again, and to check exactly what will change.",
    "Los cambios de seguridad requieren que vuelvas a confirmar tu identidad y revises exactamente qué cambiará.",
    "Les changements de sécurité exigent de confirmer à nouveau votre identité et de vérifier précisément ce qui changera.",
    "Für Sicherheitsänderungen müssen Sie sich erneut bestätigen und genau prüfen, was sich ändert.",
    "セキュリティ設定の変更には、ご本人の再確認と、変更内容の確認が必要です。",
  ],
  A03: [
    "Check retention reasons and expiry. Export access expires; deletion may retain legally held records.",
    "Consulta motivos y plazos de retención. La exportación caduca; puede haber conservación legal.",
    "Vérifiez les motifs et délais de conservation. L’export expire ; une obligation légale peut empêcher l’effacement.",
    "Aufbewahrungsgrund und Frist prüfen. Exporte laufen ab; rechtlich gesperrte Daten bleiben erhalten.",
    "保持理由と期限を確認してください。書き出しには期限があり、法的保持対象は残る場合があります。",
  ],
  A04: [
    "If access was revoked or something changed, review and approve it again. A test run doesn’t give permission.",
    "Si se revocó el acceso o algo cambió, revísalo y apruébalo de nuevo. Una prueba no concede permiso.",
    "Si l’accès a été révoqué ou si quelque chose a changé, vérifiez et approuvez à nouveau. Un essai ne donne aucune autorisation.",
    "Wenn der Zugriff widerrufen wurde oder sich etwas geändert hat, prüfen und genehmigen Sie erneut. Ein Testlauf erteilt keine Berechtigung.",
    "アクセスが取り消されたり内容が変わったりした場合は、もう一度確認して承認してください。テスト実行では権限は付与されません。",
  ],
  S01: [
    "Your sign-up draft is kept while we check your identity, country and payment setup.",
    "Tu borrador de registro se conserva mientras comprobamos tu identidad, tu país y la configuración de pagos.",
    "Votre brouillon d’inscription est conservé pendant la vérification de votre identité, de votre pays et de vos paiements.",
    "Ihr Anmeldeentwurf bleibt erhalten, während wir Identität, Land und Zahlungseinrichtung prüfen.",
    "本人確認・国・支払い設定を確認している間も、登録の下書きは保持されます。",
  ],
  S02: [
    "Check when each item was last updated, and deal with orders that are already paid first.",
    "Mira cuándo se actualizó cada elemento y atiende primero los pedidos ya pagados.",
    "Vérifiez la date de mise à jour de chaque élément et traitez d’abord les commandes déjà payées.",
    "Prüfen Sie, wann jeder Eintrag zuletzt aktualisiert wurde, und bearbeiten Sie zuerst bereits bezahlte Bestellungen.",
    "各項目の最終更新日時を確認し、支払い済みの注文から先に対応してください。",
  ],
  S03: [
    "Fix the rows with errors, preview the drafts that passed, then confirm or undo the import.",
    "Corrige las filas con errores, previsualiza los borradores válidos y después confirma o deshaz la importación.",
    "Corrigez les lignes en erreur, prévisualisez les brouillons acceptés, puis confirmez ou annulez l’import.",
    "Korrigieren Sie fehlerhafte Zeilen, sehen Sie sich die angenommenen Entwürfe an und bestätigen oder widerrufen Sie dann den Import.",
    "エラーのある行を修正し、問題のない下書きをプレビューしてから、インポートを確定するか取り消してください。",
  ],
  S04: [
    "Fill in the required item details and wait for uploads to pass their safety scan before asking to publish.",
    "Completa los datos obligatorios del artículo y espera a que las subidas pasen el análisis de seguridad antes de solicitar la publicación.",
    "Renseignez les informations obligatoires et attendez que les fichiers passent l’analyse de sécurité avant de demander la publication.",
    "Füllen Sie die Pflichtangaben aus und warten Sie, bis Uploads die Sicherheitsprüfung bestanden haben, bevor Sie die Veröffentlichung anfragen.",
    "公開を申請する前に、必須の商品情報を入力し、アップロードの安全チェックが終わるまでお待ちください。",
  ],
  S05: [
    "Check which policy is in effect and any ongoing billing before pausing or reopening your shop.",
    "Comprueba qué política está vigente y si hay facturación periódica antes de pausar o reabrir tu tienda.",
    "Vérifiez les conditions en vigueur et toute facturation récurrente avant de suspendre ou de rouvrir votre boutique.",
    "Prüfen Sie die geltende Richtlinie und laufende Abrechnungen, bevor Sie Ihren Shop pausieren oder wieder öffnen.",
    "ショップを一時停止・再開する前に、適用中のポリシーと継続中の請求をご確認ください。",
  ],
  S06: [
    "If capacity or the proof changes, send an updated promise and ask the buyer to accept it again.",
    "Si cambian la capacidad o la prueba, envía un compromiso actualizado y pide al comprador que lo acepte de nuevo.",
    "Si la capacité ou la maquette change, envoyez un engagement mis à jour et demandez à l’acheteur de l’accepter à nouveau.",
    "Wenn sich Kapazität oder Entwurf ändern, senden Sie eine aktualisierte Zusage und bitten Sie den Käufer, erneut zuzustimmen.",
    "制作枠や校正が変わった場合は、更新した約束内容を送り、購入者にもう一度承認してもらってください。",
  ],
  S07: [
    "Check whether the label was already bought before trying again. A tracking link alone doesn’t confirm delivery.",
    "Comprueba si la etiqueta ya se compró antes de reintentar. Un enlace de seguimiento por sí solo no confirma la entrega.",
    "Vérifiez si l’étiquette a déjà été achetée avant de réessayer. Un lien de suivi ne confirme pas à lui seul la livraison.",
    "Prüfen Sie vor einem neuen Versuch, ob das Etikett bereits gekauft wurde. Ein Tracking-Link allein bestätigt keine Zustellung.",
    "再試行する前に、ラベルが購入済みかどうかをご確認ください。追跡リンクだけでは配達完了は確認できません。",
  ],
  S08: [
    "“Transferred”, “waiting for bank” and “paid out” are different stages. Use the payment reference if amounts don’t match.",
    "«Transferido», «pendiente del banco» y «abonado» son etapas distintas. Usa la referencia del pago si los importes no coinciden.",
    "« Transféré », « en attente de la banque » et « versé » sont des étapes différentes. Utilisez la référence du paiement si les montants ne correspondent pas.",
    "„Überwiesen“, „Bank ausstehend“ und „ausgezahlt“ sind verschiedene Schritte. Nutzen Sie die Zahlungsreferenz, wenn Beträge nicht übereinstimmen.",
    "「送金済み」「銀行確認待ち」「入金済み」は別の段階です。金額が合わない場合は支払いの参照番号をご利用ください。",
  ],
  S09: [
    "Wait for any unconfirmed refunds first, then check what is still refundable for each item before deciding again.",
    "Espera primero a los reembolsos sin confirmar y revisa lo que aún se puede reembolsar de cada artículo antes de decidir de nuevo.",
    "Attendez d’abord les remboursements non confirmés, puis vérifiez ce qui reste remboursable pour chaque article avant de décider à nouveau.",
    "Warten Sie zuerst unbestätigte Erstattungen ab und prüfen Sie dann, was pro Artikel noch erstattet werden kann, bevor Sie erneut entscheiden.",
    "まず未確定の返金を待ち、商品ごとに返金できる残りを確認してから、改めて判断してください。",
  ],
  S10: [
    "Campaigns can’t start or spend money until rates and terms are approved and there’s budget left.",
    "Las campañas no pueden empezar ni gastar dinero hasta que se aprueben las tarifas y condiciones y quede presupuesto.",
    "Les campagnes ne peuvent ni démarrer ni dépenser tant que les tarifs et conditions ne sont pas approuvés et qu’il ne reste pas de budget.",
    "Kampagnen können erst starten oder Geld ausgeben, wenn Preise und Bedingungen genehmigt sind und noch Budget vorhanden ist.",
    "料金と条件が承認され、予算が残っていない限り、キャンペーンの開始や支出はできません。",
  ],
  S11: [
    "Read the original message and check how it was translated. Scheduled replies can’t change anyone’s account.",
    "Lee el mensaje original y comprueba cómo se tradujo. Las respuestas programadas no pueden cambiar ninguna cuenta.",
    "Lisez le message d’origine et vérifiez sa traduction. Les réponses programmées ne peuvent modifier aucun compte.",
    "Lesen Sie die Originalnachricht und prüfen Sie die Übersetzung. Geplante Antworten können kein Konto ändern.",
    "元のメッセージと翻訳の内容をご確認ください。予約した返信でアカウントが変更されることはありません。",
  ],
  S12: [
    "You’ll only see evidence you’re allowed to view. Check the decision that applies and how you can appeal.",
    "Solo verás las pruebas que puedes consultar. Revisa la decisión que se aplica y cómo puedes recurrir.",
    "Vous ne voyez que les preuves que vous pouvez consulter. Vérifiez la décision applicable et vos possibilités de recours.",
    "Sie sehen nur Nachweise, die Sie einsehen dürfen. Prüfen Sie die geltende Entscheidung und Ihre Einspruchsmöglichkeiten.",
    "閲覧が許可された資料だけが表示されます。適用される判断と異議申し立ての方法をご確認ください。",
  ],
  O01: [
    "Support access doesn’t give you control over bank details, payments or payouts.",
    "El acceso de soporte no te da control sobre datos bancarios, pagos ni liquidaciones.",
    "L’accès assistance ne donne aucun contrôle sur les coordonnées bancaires, les paiements ou les versements.",
    "Supportzugriff gibt Ihnen keine Kontrolle über Bankdaten, Zahlungen oder Auszahlungen.",
    "サポート用のアクセスでは、銀行情報・支払い・入金を操作することはできません。",
  ],
  O02: [
    "Set unsafe evidence aside or reassign a conflict, then record the reason and send the notice.",
    "Aparta las pruebas no seguras o reasigna un conflicto, y después registra el motivo y envía el aviso.",
    "Isolez les preuves dangereuses ou réattribuez un conflit, puis consignez le motif et envoyez l’avis.",
    "Stellen Sie unsichere Nachweise zurück oder weisen Sie einen Konflikt neu zu, und erfassen Sie dann Grund und Hinweis.",
    "安全でない資料を隔離するか担当を変更し、理由を記録して通知を送ってください。",
  ],
  O03: [
    "Hold unmatched money until it’s reconciled, and get approval from a second reviewer.",
    "Retén el dinero sin conciliar hasta que se concilie y obtén la aprobación de un segundo revisor.",
    "Bloquez les fonds non rapprochés jusqu’au rapprochement et obtenez l’approbation d’un second vérificateur.",
    "Halten Sie nicht zugeordnetes Geld bis zum Abgleich zurück und holen Sie die Freigabe einer zweiten Prüfperson ein.",
    "照合が済むまで不一致の資金を保留し、2人目の確認者の承認を得てください。",
  ],
  O04: [
    "Pending country changes need up-to-date evidence and a second person’s approval. A proposal isn’t an approval.",
    "Los cambios de país pendientes necesitan pruebas actualizadas y la aprobación de una segunda persona. Una propuesta no es una aprobación.",
    "Les changements de pays en attente exigent des preuves à jour et l’approbation d’une seconde personne. Une proposition n’est pas une approbation.",
    "Ausstehende Länderänderungen brauchen aktuelle Nachweise und die Freigabe einer zweiten Person. Ein Vorschlag ist keine Genehmigung.",
    "保留中の国設定の変更には、最新の資料と2人目の承認が必要です。提案は承認ではありません。",
  ],
  O05: [
    "While new charges are paused, settle existing orders first. Restoring data needs approval before anything is overwritten.",
    "Mientras los nuevos cargos están en pausa, resuelve primero los pedidos existentes. Restaurar datos requiere aprobación antes de sobrescribir nada.",
    "Pendant la pause des nouveaux débits, traitez d’abord les commandes existantes. Toute restauration exige une approbation avant d’écraser des données.",
    "Solange neue Belastungen pausiert sind, erledigen Sie zuerst bestehende Bestellungen. Eine Wiederherstellung braucht eine Freigabe, bevor etwas überschrieben wird.",
    "新規請求の停止中は、まず既存の注文を処理してください。データの復元には、上書きする前に承認が必要です。",
  ],
  O06: [
    "Retain only the documented legal-hold scope and review its expiry.",
    "Conserva solo el alcance documentado de la retención legal y revisa su vencimiento.",
    "Conservez uniquement le périmètre légal documenté et vérifiez son expiration.",
    "Nur dokumentierten Umfang der rechtlichen Aufbewahrung behalten und Ablauf prüfen.",
    "文書化された法的保持範囲のみ保持し、期限を確認してください。",
  ],
  I01: [
    "Use the returned rate-limit or scope reason. Never request a broader token to bypass a denial.",
    "Usa el motivo de límite o alcance devuelto. No amplíes el token para evitar una denegación.",
    "Utilisez le motif de limite ou de portée fourni. N’élargissez pas le jeton pour contourner un refus.",
    "Zurückgegebenen Limit- oder Geltungsgrund nutzen. Ablehnungen nicht durch breitere Token umgehen.",
    "返された制限理由を確認してください。拒否を回避するために権限範囲を広げないでください。",
  ],
} satisfies Record<string, readonly string[]>;
export function recoveryText(
  screen: string,
  locale: LaunchLocale,
): string | undefined {
  return messages[screen as keyof typeof messages]?.[
    ["en", "es", "fr", "de", "ja"].indexOf(locale)
  ];
}
/** Only user-selected search filters are removed. Destination is never changed implicitly. */
export function filterRecoveryLinks(
  path: string,
  query: string,
): { name: string; href: string }[] {
  const current = new URLSearchParams(query);
  return ["q", "kind", "sort"]
    .filter((key) => current.has(key))
    .map((name) => {
      const next = new URLSearchParams(current);
      next.delete(name);
      next.delete("cursor");
      return { name, href: path + (next.size ? "?" + next.toString() : "") };
    });
}
